import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send, RotateCcw, Sparkles } from "lucide-react";
import { API } from "@/lib/api";
import { COMPANY, QUICK_TOPICS } from "@/lib/content";
import { RichText } from "./RichText";

const EMERGENCY = /\b(911|emergency|chest pain|stroke|not breathing|unconscious|overdose|suicid)/i;
const sid = () => {
  let s = sessionStorage.getItem("elevion_chat_sid");
  if (!s) { s = `s-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`; sessionStorage.setItem("elevion_chat_sid", s); }
  return s;
};
const WELCOME = { role: "bot", kind: "info", text: "Hi! I'm the **Elevion AI Assistant** — an AI that answers from Elevion's verified website information. Ask about services, staffing roles, the HR checklist or how to apply.\n\nPlease don't share medical, identity or Social Security details here. In an emergency, call 911." };

async function streamChat(message, onDelta) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), 45000);
  try {
    const r = await fetch(`${API}/chat`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: sid(), message }), signal: ctrl.signal });
    if (r.status === 429) throw new Error("limit");
    if (!r.ok || !r.body) throw new Error("down");
    const reader = r.body.getReader();
    const dec = new TextDecoder();
    let buf = "", got = false;
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      const parts = buf.split("\n\n");
      buf = parts.pop();
      for (const p of parts) {
        if (!p.startsWith("data: ")) continue;
        const ev = JSON.parse(p.slice(6));
        if (ev.error) throw new Error("down");
        if (ev.delta) { got = true; onDelta(ev.delta); }
      }
    }
    if (!got) throw new Error("down");
  } catch (e) {
    throw new Error(e.name === "AbortError" ? "timeout" : e.message);
  } finally { clearTimeout(t); }
}

const Handoff = ({ q }) => (
  <div className="mt-2.5 pt-2 border-t border-[#154F49]/15 text-xs text-[#465F57] flex flex-wrap gap-x-2 gap-y-1">
    <span>Need a person?</span>
    <a href={`mailto:${COMPANY.email}?subject=${encodeURIComponent("Website inquiry: " + (q || "").slice(0, 60))}`} className="font-bold text-[#154F49] underline">Email {COMPANY.email}</a>
    <a href={COMPANY.phoneHref} className="font-bold text-[#154F49] underline">Call {COMPANY.phoneDisplay}</a>
  </div>
);

const Bubble = ({ m, onRetry }) => (
  <div className={`max-w-[88%] p-3.5 rounded-2xl text-sm leading-relaxed ${m.role === "user" ? "ml-auto bg-[#154F49] text-[#FBF8F1] rounded-br-md" : "bg-white text-[#1A2E2B] border border-[#154F49]/15 rounded-bl-md"}`} data-testid={`chat-msg-${m.role}`}>
    {m.role === "bot" && <p className="text-[11px] font-bold uppercase tracking-wider text-[#9B742A] mb-1">{m.kind === "ai" ? "AI Assistant" : m.kind === "error" ? "Connection notice" : "Verified info"}</p>}
    {m.text ? <RichText text={m.text} /> : <span className="inline-flex gap-1" aria-label="AI is typing"><i className="dot" /><i className="dot" /><i className="dot" /></span>}
    {m.kind === "error" && <button type="button" onClick={onRetry} className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#154F49] text-[#FBF8F1] text-xs font-bold" data-testid="chat-retry-btn"><RotateCcw className="w-3.5 h-3.5" /> Retry</button>}
    {m.role === "bot" && m.kind !== "info" && m.done !== false && <Handoff q={m.q} />}
  </div>
);

export const ChatWidget = ({ open, setOpen }) => {
  const [msgs, setMsgs] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const lastQ = useRef("");
  const logRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { if (open) { const t = setTimeout(() => inputRef.current?.focus(), 150); return () => clearTimeout(t); } return undefined; }, [open]);
  useEffect(() => { const el = logRef.current; if (el) el.scrollTop = el.scrollHeight; }, [msgs]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && open) setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, setOpen]);

  const patchLast = (fn) => setMsgs((p) => { const c = [...p]; c[c.length - 1] = fn(c[c.length - 1]); return c; });

  const ask = async (q, isRetry = false) => {
    const text = q.trim();
    if (!text || busy) return;
    lastQ.current = text;
    setInput("");
    if (isRetry) setMsgs((p) => p.slice(0, -1));
    else setMsgs((p) => [...p, { role: "user", text }]);
    if (EMERGENCY.test(text)) {
      setMsgs((p) => [...p, { role: "bot", kind: "info", text: "**If someone may be having a medical emergency, call 911 now.** This AI assistant can't help with emergencies or give medical advice." }]);
      return;
    }
    setBusy(true);
    setMsgs((p) => [...p, { role: "bot", kind: "ai", text: "", q: text, done: false }]);
    try {
      await streamChat(text, (d) => patchLast((m) => ({ ...m, text: m.text + d })));
      patchLast((m) => ({ ...m, done: true }));
    } catch (e) {
      const why = e.message === "limit" ? "You've sent several messages quickly and reached the usage limit. Please wait a minute and retry." : e.message === "timeout" ? "The AI service took too long to respond." : "The AI service is unavailable right now.";
      patchLast(() => ({ role: "bot", kind: "error", text: `${why} You can retry, or reach Elevion directly at ${COMPANY.email} or (202) 710-0131.`, q: text }));
    } finally { setBusy(false); }
  };

  const quick = (t) => setMsgs((p) => [...p, { role: "user", text: t.label }, { role: "bot", kind: "verified", text: t.answer, q: t.label }]);

  return (
    <div className="no-print">
      <AnimatePresence>
        {!open && (
          <motion.button initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.6, opacity: 0 }} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} type="button" onClick={() => setOpen(true)} aria-label="Open Elevion AI Assistant chat" data-testid="chat-open-button"
            className="hidden lg:flex fixed bottom-5 right-5 z-50 items-center gap-2 pl-4 pr-5 py-3.5 rounded-full bg-[#154F49] text-[#FBF8F1] shadow-[0_16px_40px_-10px_rgba(21,79,73,0.7)] border border-[#CDA34F]/60">
            <span className="relative"><MessageCircle className="w-5 h-5" /><span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-[#CDA34F] animate-ping" /></span>
            <span className="text-sm font-semibold">Ask our AI</span>
          </motion.button>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {open && (
          <motion.div role="dialog" aria-modal="false" aria-labelledby="chat-title" initial={{ opacity: 0, y: 24, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.97 }} transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }} data-testid="chat-panel" data-lenis-prevent
            className="fixed z-50 inset-x-3 bottom-3 sm:inset-x-auto sm:right-5 sm:bottom-5 sm:w-[400px] h-[min(640px,calc(100dvh-24px))] flex flex-col rounded-3xl bg-[#FBF8F1] border border-[#154F49]/25 shadow-2xl overflow-hidden">
            <div className="bg-[#154F49] text-[#FBF8F1] px-5 py-4 flex items-start justify-between gap-3">
              <div>
                <p id="chat-title" className="font-serif text-2xl leading-none flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#E2C372]" /> Elevion AI Assistant</p>
                <p className="text-[11px] text-[#D6E0D9] mt-1.5">AI-generated answers grounded in verified Elevion info. Not medical advice.</p>
              </div>
              <button type="button" onClick={() => setOpen(false)} aria-label="Close chat" data-testid="chat-close-button" className="w-10 h-10 shrink-0 rounded-full bg-[#FBF8F1]/10 hover:bg-[#FBF8F1]/20 flex items-center justify-center focus-visible:ring-2 focus-visible:ring-[#CDA34F]"><X className="w-5 h-5" /></button>
            </div>
            <div ref={logRef} className="flex-1 overflow-y-auto p-4 space-y-3" aria-live="polite" aria-busy={busy} data-testid="chat-log">
              {msgs.map((m, i) => <Bubble key={i} m={m} onRetry={() => ask(lastQ.current, true)} />)}
            </div>
            <div className="px-4 pt-2 flex gap-2 overflow-x-auto" aria-label="Quick topics">
              {QUICK_TOPICS.map((t) => (
                <button key={t.key} type="button" onClick={() => quick(t)} data-testid={`chat-topic-${t.key}`} className="shrink-0 px-3 py-1.5 rounded-full border border-[#154F49]/25 text-xs font-semibold text-[#154F49] hover:bg-[#EDF1E6]">{t.label}</button>
              ))}
            </div>
            <form onSubmit={(e) => { e.preventDefault(); ask(input); }} className="p-3 flex gap-2">
              <label htmlFor="chat-input" className="sr-only">Type your question</label>
              <input id="chat-input" ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} maxLength={1500} placeholder="Ask about roles, services, applying…" data-testid="chat-input"
                className="flex-1 px-4 py-3 rounded-full border border-[#154F49]/30 bg-white text-sm text-[#1A2E2B] placeholder:text-[#5C706B] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#CDA34F]" />
              <button type="submit" disabled={busy || !input.trim()} aria-label="Send message" data-testid="chat-send-button" className="w-12 h-12 rounded-full bg-[#CDA34F] text-[#154F49] flex items-center justify-center disabled:opacity-50"><Send className="w-5 h-5" /></button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
